/**
 * Zod-схемы, типы DTO и зеркала перечислений «работы» — Блок 2
 * «Трекер объектов», Ф2 · В1 (миграция 046).
 *
 * «Работа» — ОДНА сущность на два вида: проектная работа
 * (`PROJECT_WORK` — «сотрудник ведёт раздел объекта в такой-то период») и
 * задача (`TASK`). Различает их `kind`, и именно от него зависит, какие
 * поля обязательны.
 *
 * ─── ЧТО ЗДЕСЬ ЕСТЬ, А ЧЕГО НЕТ ───────────────────────────────────────────
 *
 * Волна В1 — владелец БД: она заводит СХЕМУ и КОНТРАКТ, но не маршруты.
 * Маршруты (`work/`, `/projects/:id/team`) приезжают в В2a и В2b и
 * пользуются схемами отсюда. Поэтому здесь нет ни одного `*QuerySchema` для
 * несуществующих списков: пустая схема под будущий маршрут выглядит рабочим
 * контрактом и однажды им становится.
 *
 * ─── ЗЕРКАЛА ПЕРЕЧИСЛЕНИЙ ─────────────────────────────────────────────────
 *
 * Значения ОБЯЗАНЫ совпадать с типами Postgres из миграции 046. Расхождение
 * не диагностируется ничем на стороне TypeScript: строка просто не пройдёт
 * запись с 22P02 в рантайме.
 *
 * `AT_CLIENT` в `WorkStatus` НЕТ — состояние «у заказчика» отменено
 * заказчиком 30.07.2026. `CANCELLED` / `ON_HOLD` не вводились никогда: снятие
 * работы — это АРХИВАЦИЯ, а не статус (решение 6), и `DELETE /works/:id`
 * не выпускается вовсе.
 *
 * ─── СРОЧНОСТЬ НЕ ХРАНИТСЯ ────────────────────────────────────────────────
 *
 * Колонки срочности в таблице нет и не будет: приоритетность убрана совсем
 * (решение заказчика). Срочность ВЫВОДИТСЯ из срока — `isUrgent` ниже. Функция
 * ЧИСТАЯ и берёт «сегодня» ПАРАМЕТРОМ: единственный источник «сегодня» на
 * сервере — `apps/api/src/common/date/today-msk.ts` (его считает Postgres в
 * `Europe/Moscow`). Если бы функция звала `new Date()` сама, с 00:00 до 03:00
 * MSK она расходилась бы с сервером на сутки, и «просрочено» в сводке
 * перестало бы сходиться с чипами на карточке.
 */
import { z } from 'zod';
import { ruEnum } from './lib/zod-ru';

// ─── Зеркала перечислений БД ──────────────────────────────────────────────

export const WorkKind = {
  /** Проектная работа: проект И раздел обязательны (CHECK-и 1 и 2 в 046). */
  PROJECT_WORK: 'PROJECT_WORK',
  /** Задача: проект и раздел необязательны; раздел БЕЗ проекта запрещён. */
  TASK: 'TASK',
} as const;
export type WorkKind = (typeof WorkKind)[keyof typeof WorkKind];

export const WorkStatus = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  ON_REVIEW: 'ON_REVIEW',
  DONE: 'DONE',
} as const;
export type WorkStatus = (typeof WorkStatus)[keyof typeof WorkStatus];

// ⚠️ `WorkStage` / `WORK_STAGE_LABEL` СНЯТЫ 11.08.2026 по решению заказчика
// (DECISIONS.md, Р-1) вместе с колонкой `Work.stage` — миграция 053. ПД и РД
// теперь два разных ОБЪЕКТА, а не признак внутри одного. Восстанавливать
// перечисление «на всякий случай» не надо: пока его нет, `.strict()`-схемы
// ниже отвергают поле `stage` во ВХОДЯЩЕМ запросе, и это проверяемо тестом.

/** Подписи состояний для интерфейса. Ключи покрывают перечисление ЦЕЛИКОМ —
 *  `Record<WorkStatus, string>` не даст добавить значение и забыть подпись. */
export const WORK_STATUS_LABEL: Readonly<Record<WorkStatus, string>> = Object.freeze({
  NOT_STARTED: 'Не начато',
  IN_PROGRESS: 'В работе',
  ON_REVIEW: 'На проверке',
  DONE: 'Готово',
});

// ─── Примитивы ────────────────────────────────────────────────────────────

const UuidSchema = z.string().uuid();
const IsoDateTimeSchema = z.string().datetime({ offset: true });

/** `YYYY-MM-DD`. Синтаксическая проверка, как в `workload.ts`: календарную
 *  достоверность и границу суток проверяет сервер по `Europe/Moscow`. */
const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата должна быть в формате ГГГГ-ММ-ДД');

/**
 * Название работы — 1..300 после обрезки пробелов, ЗЕРКАЛО CHECK-а
 * `work_title_length` из миграции 046.
 *
 * ПУСТОЕ НАЗВАНИЕ — ЛЕГАЛЬНОЕ СОСТОЯНИЕ, а не ошибка ввода: интерфейс в этом
 * случае показывает АКТУАЛЬНОЕ имя раздела из каталога. Прежнее умолчание
 * «название = снимок имени раздела» отменено — снимок заводил второй источник
 * имени заново, ровно ту картину, ради устранения которой заказчик отменил
 * KD-5.
 *
 * Поэтому `''` НОРМАЛИЗУЕТСЯ В `undefined` до проверки длины: без этого пустое
 * поле формы (а `<input>` всегда даёт `''`, не `undefined`) упиралось бы в
 * «минимум 1 символ» и заставляло бы что-нибудь придумать.
 */
const TitleSchema = z.preprocess((v) => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}, z.string().min(1).max(300, 'Название не длиннее 300 символов').optional());

const DescriptionSchema = z.preprocess((v) => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}, z.string().max(2000, 'Описание не длиннее 2000 символов').optional());

/**
 * Оценка трудоёмкости — зеркало `Decimal(5,1)` + CHECK «строго положительна».
 *
 * ⚠️ ПОЛЯ НЕТ НИ В ОДНОМ ОТВЕТЕ СЕРВЕРА, и это не упущение: значение
 * МАСКИРУЕТСЯ даже в снимке аудита (`SENSITIVE_FIELDS.work`). Схема нужна
 * пишущей стороне; в `WorkResponseSchema` ниже его сознательно нет.
 */
const HoursEstimateSchema = z
  .number()
  .positive('Оценка часов должна быть больше нуля')
  .max(9999.9, 'Оценка часов не больше 9999,9')
  .multipleOf(0.1, 'Оценка часов — с точностью до десятых');

// ─── Правило периода ──────────────────────────────────────────────────────

/**
 * ═══ ПОРЯДОК АРГУМЕНТОВ В ЭТОМ МОДУЛЕ — ПРАВИЛО, А НЕ ПРИВЫЧКА ════════════
 *
 * Все функции «дата работы против сегодня» принимают их В ОДНОМ порядке:
 * **сначала дата работы, вторым `today`** — `isUrgent(dueDate, today)`,
 * `daysUntilIso(dueDate, today)`, `isOverdue(dueDate, today, status)`,
 * `urgencyTone(dueDate, today, status)`.
 *
 * Правило записано потому, что нарушить его НЕЛЬЗЯ ЗАМЕТИТЬ: два аргумента
 * одного типа подряд, перестановка компилируется, знак результата молча
 * инвертируется. Этот класс уже стоил разбора в Ф2 (разрешённый `sectionId`
 * против `dto.sectionId` в одной позиции).
 *
 * `isPeriodValid` из этого правила ВЫПАДАЛА — она сравнивает две даты работы
 * между собой, и «первой идёт дата работы» её не дисциплинирует никак. Поэтому
 * у неё **перестановка сделана невыразимой**: аргумент один, именованный.
 * Позиционную форму `isPeriodValid(start, due)` заменить на `(due, start)` было
 * молчаливой правкой; `{ startDate, dueDate }` переставить местами невозможно.
 *
 * Остальные функции удерживаются тестами с АСИММЕТРИЧНЫМИ фикстурами: у них
 * перестановка даёт другой результат, а не тот же (`test/work-aggregate.test.ts`,
 * набор «порядок аргументов»).
 */

/**
 * `dueDate >= startDate` — зеркало CHECK-а `work_dates_valid`.
 *
 * Вынесено отдельной функцией, а не повторено в трёх схемах: сравнение строк
 * `YYYY-MM-DD` лексикографически КОРРЕКТНО (формат с ведущими нулями и
 * фиксированной шириной), и делать здесь `new Date()` было бы ошибкой — она
 * втащила бы часовой пояс в проверку, которой пояс не нужен.
 *
 * ⚠️ ПРИНИМАЕТ ОБЪЕКТ, А НЕ ДВЕ ПОЗИЦИИ (Ф3 · В3a) — см. врезку выше.
 */
export function isPeriodValid(period: { startDate: string; dueDate: string }): boolean {
  return period.dueDate >= period.startDate;
}

/**
 * Строка — календарная дата `YYYY-MM-DD`, существующая в календаре.
 *
 * Синтаксис проверяется регулярным выражением, СУЩЕСТВОВАНИЕ — обратным
 * преобразованием: `Date.parse('2026-02-31T00:00:00Z')` даёт 3 марта, то есть
 * молча «чинит» несуществующую дату. Сравнение исходной строки с обратно
 * отформатированной ловит и это.
 */
export function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const ms = Date.parse(`${value}T00:00:00Z`);
  if (Number.isNaN(ms)) return false;
  return new Date(ms).toISOString().slice(0, 10) === value;
}

/**
 * Отвергнуть негодную дату ГРОМКО.
 *
 * ⚠️ ЗДЕСЬ ИМЕННО ИСКЛЮЧЕНИЕ, А НЕ ТИХОЕ УМОЛЧАНИЕ, и это выбор (Ф3 · В3a,
 * находка круга ревью). Прежде пустой или кривой `today` давал «срок сегодня» и
 * ПОГАШЕННЫЙ чип — то есть отказ выглядел как «всё спокойно», а это худший из
 * возможных исходов: пользователь видит исправный экран и не знает, что даты на
 * нём не значат ничего.
 *
 * Цена исключения известна и приемлема: обе даты в базе NOT NULL, а `today`
 * приходит из zod-проверенного ответа сервера — значит негодное значение здесь
 * это ДЕФЕКТ КОДА, а не ввод пользователя. Дефект кода обязан быть виден: на
 * клиенте исключение поднимет `app-error-boundary`, и экран сломается ЗАМЕТНО,
 * а не соврёт тихо.
 */
function assertCalendarDate(value: string, label: string): void {
  if (!isCalendarDate(value)) {
    throw new RangeError(
      `${label}: ожидалась календарная дата ГГГГ-ММ-ДД, получено ${JSON.stringify(value)}`,
    );
  }
}

/**
 * Текст ошибки периода.
 *
 * ЭКСПОРТИРУЕТСЯ (Ф3 · В3a): его показывает не только схема, но и форма —
 * `components/work/period-fields.tsx` краснеет ДО отправки запроса. Копия в
 * компоненте разошлась бы со схемой на первой же редактуре, и пользователь
 * получал бы два разных текста на одно правило в зависимости от того, где
 * оно сработало.
 *
 * ⚠️ КОПИИ ЭТОЙ СТРОКИ В РЕПОЗИТОРИИ ЕСТЬ И ДО ВОЛНЫ — `project-team.ts:69`,
 * `work.service.ts:520`, `project-team.service.ts:200`. Они НЕ сводятся здесь:
 * два из трёх файлов принадлежат другим волнам (§2.1 плана фазы), и правка в
 * них была бы вторжением в чужую поверхность. Названо, а не сделано молча.
 */
export const PERIOD_MESSAGE = 'Срок не может быть раньше начала';

// ─── Срочность (производная, НЕ хранится) ─────────────────────────────────

/**
 * Работа срочная, если её срок — сегодня или завтра.
 *
 * ЧИСТАЯ функция: «сегодня» приходит ПАРАМЕТРОМ. Единственный источник на
 * сервере — `queryTodayMsk` (считает Postgres в `Europe/Moscow`); на клиенте —
 * дата, пришедшая с сервером в ответе. Оба аргумента — `YYYY-MM-DD`.
 *
 * ПОЧЕМУ БЕЗ `new Date()`. Сложение суток через `Date` тянет часовой пояс
 * исполнителя: в браузере в Москве и на сервере в UTC «завтра» с 00:00 до
 * 03:00 MSK разное. Здесь сутки прибавляются к КАЛЕНДАРНОЙ дате через UTC-полночь
 * — единственная точка, где `Date` допустим, потому что и вход, и выход — чистая
 * календарная дата без времени.
 */
export function isUrgent(dueDate: string, today: string): boolean {
  return dueDate <= addDaysIso(today, 1);
}

/** `YYYY-MM-DD` + n суток, без участия часового пояса вызывающего. */
export function addDaysIso(iso: string, days: number): string {
  const ms = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms + days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Полных суток от `today` до `dueDate`. Отрицательное — срок в прошлом.
 *
 * Нужна подсказке периода («просрочено N дн / сегодня / через N дн») и
 * подписи полосы. Живёт ЗДЕСЬ, а не в компоненте, по той же причине, что
 * `isUrgent`: вычитание через `Date` в браузере втащило бы часовой пояс, и
 * «через 1 день» с 00:00 до 03:00 MSK давало бы «сегодня».
 *
 * Обе даты — календарные `YYYY-MM-DD`; разность считается через UTC-полночь,
 * то есть на переходах летнего времени не плавает (в `Europe/Moscow` его нет
 * с 2014 года, но функция общая и обязана быть верной без этой оговорки).
 */
export function daysUntilIso(dueDate: string, today: string): number {
  // ⚠️ ПРЕЖДЕ ЗДЕСЬ СТОЯЛО `return 0` НА НЕГОДНОМ ВХОДЕ — то есть кривая дата
  // печаталась как «срок сегодня». Разбор — докблок `assertCalendarDate`.
  assertCalendarDate(dueDate, 'dueDate');
  assertCalendarDate(today, 'today');
  const due = Date.parse(`${dueDate}T00:00:00Z`);
  const now = Date.parse(`${today}T00:00:00Z`);
  return Math.round((due - now) / 86_400_000);
}

/**
 * Работа НЕ ЗАКРЫТА — ОБЩИЙ предикат «незакрытости» ДЛЯ СВОДКИ.
 *
 * ═══ ⚠️ «ЕДИНСТВЕННОЕ ОПРЕДЕЛЕНИЕ В СИСТЕМЕ» — ЛОЖЬ, И ОНА БЫЛА ЗДЕСЬ ═════
 *
 * 🚫 Прежняя редакция называла эту функцию ~~«единственным определением
 * „незакрытости" в системе»~~ и перечисляла ровно троих потребителей.
 * **Проверено грепом — прежних мест как минимум ЧЕТЫРЕ, и ни одно не
 * переведено:**
 *
 *   | адрес                                            | форма                          |
 *   | ------------------------------------------------ | ------------------------------ |
 *   | `apps/api/src/projects/projects.service.ts:1755`  | `status: { not: DONE }` в `where` |
 *   | `apps/web/src/pages/tasks/board-model.ts:156`     | `status !== WorkStatus.DONE`   |
 *   | `apps/web/src/components/gantt/gantt-lib.ts:94`   | `bar.status !== 'DONE'`        |
 *   | `apps/web/src/pages/my-work/my-work.page.tsx:244` | `status === DONE ? … : …`      |
 *
 * Чинить их фикс-пак В13′ не обязан и не чинит — это **долг**, названный в
 * отчёте фикс-пака 2 для реестра. Но утверждать единственность, которой нет,
 * нельзя: следующий читатель обопрётся на неё и решит, что правку правила
 * достаточно внести здесь.
 *
 * ═══ ЗАЧЕМ ОТДЕЛЬНАЯ ФУНКЦИЯ РАДИ ОДНОГО СРАВНЕНИЯ ═══════════════════════
 *
 * Сравнение `status !== DONE` само по себе тривиально; дорого не оно, а то,
 * что каждый следующий потребитель пишет его заново — и рано или поздно один
 * из них напишет `status === IN_PROGRESS || status === NOT_STARTED`,
 * «забыв» `ON_REVIEW`. Тогда числитель и знаменатель одной и той же плитки
 * начнут считать разные множества, и ошибка будет тихой: оба числа выглядят
 * правдоподобно.
 *
 * Потребителей ЭТОЙ функции ЧЕТВЕРО: `isOverdue`, `isDueWithin`, знаменатель
 * плитки «Просрочено» (**Р-142**, `apps/api/src/summary/summary.service.ts`) и
 * гашение напоминания о сроке при доставке (Ф7 · В17,
 * `apps/api/src/bot-vkteams/vkteams-outbox-sender.ts`) — поэтому предикат
 * вынесен, а не повторён по месту четырежды. Три числа одного ряда дашборда
 * считают одно множество.
 *
 * ⚠️ ЧИСЛО ПРАВИТСЯ ВМЕСТЕ С ПОТРЕБИТЕЛЕМ, И ПРЕЖНЯЯ РЕДАКЦИЯ ЭТОГО НЕ ЖДАЛА.
 * 🚫 Она говорила ~~«потребителей трое … на остальные поверхности это правило
 * сегодня не распространено»~~ — и второе утверждение перестало быть правдой
 * раньше первого: четвёртый потребитель стоит НЕ на дашборде, а в боте. Список
 * адресов выше — не украшение: по нему судят, где правку правила искать.
 *
 * ⚠️ `aggregateSectionStatus` СЮДА НЕ ВХОДИТ, хотя `DONE` в ней упомянут:
 * там считается СЧЁТЧИК завершённых (`done += 1`), а не признак «не закрыта»,
 * и подменять один другим нельзя — счётчик отвечает на другой вопрос.
 *
 * ⚠️ МАШИННОЙ ЗАЩИТЫ У ЭТОГО ПРАВИЛА НЕТ — см. врезку у `isDueWithin`.
 * Сторож `no-second-aggregate` форму `status !== 'DONE'` по месту НЕ ловит
 * (проверено пробником: пять форм, три из них зелёные). Держится правило
 * ревью, а не гейтом, и обещать здесь машину было бы ложью.
 */
export function isOpenStatus(status: WorkStatus): boolean {
  return status !== WorkStatus.DONE;
}

/**
 * Работа СДАНА НА ПРОВЕРКУ — то есть лежит у проверяющего, а не у исполнителя.
 *
 * ═══ ЗАЧЕМ ПРЕДИКАТ РАДИ ОДНОГО СРАВНЕНИЯ — ПРИЧИНА НЕ «КРАСИВЕЕ» ════════
 *
 * Свойство состояния — того же класса, что `isOpenStatus` строкой выше, и дом
 * у него тот же: `packages/shared/src/work.ts` — единственный файл, которому
 * сторож `no-second-aggregate` разрешает заводить правила НАД шкалой
 * (`ALLOWED_RULE`, `apps/web/src/components/work/no-second-aggregate.test.ts`).
 *
 * ⚠️ ЗДЕСЬ СТОРОЖ СРАБОТАЛ ПО ДЕЛУ, И ЭТО НАДО НАЗЫВАТЬ ВСЛУХ. Ф7 · В17
 * написала гашение напоминания о сроке прямо в отправителе бота двумя
 * сравнениями подряд (`status === 'DONE'`, `status === 'ON_REVIEW'`), и сторож
 * покраснел — по собственному порогу «два РАЗНЫХ состояния в сравнении».
 * Порог сработал верно: два сравнения состояний в одном файле и есть второй
 * набор правил над шкалой, где бы он ни лежал. Предикат заведён потому, что
 * правилу здесь МЕСТО, а не потому, что так замолчал гейт: внести отправителя
 * в список исключений значило бы снять охрану с 1243 строк навсегда.
 *
 * ⚠️ ЭТО НЕ ОТРИЦАНИЕ `isOpenStatus`, И ПОДМЕНА ОДНОГО ДРУГИМ МЕНЯЕТ СМЫСЛ.
 * `!isOpenStatus(s)` — «работа закрыта» (`DONE`); здесь — «сдана, но ещё не
 * принята» (`ON_REVIEW`). Множества не пересекаются и не дополняют друг друга:
 * `NOT_STARTED` и `IN_PROGRESS` не попадают ни в одно из них.
 *
 * ⚠️ ПОТРЕБИТЕЛЬ ПОКА ОДИН — гашение напоминания о сроке при доставке
 * (`vkteams-outbox-sender.ts`, **Р-147, п. 3**: работа «на проверке»
 * напоминаний о сроке не получает). Первый рубеж того же правила стоит в
 * отборе крона (`w."status" <> 'ON_REVIEW'`, `due-soon-query.sql.ts`) и через
 * этот предикат НЕ идёт — он на SQL, и переписать его сюда нельзя.
 * То есть определение здесь единственное для TypeScript, но не для системы;
 * обещать большее было бы той же ложью, что разобрана у `isOpenStatus`.
 *
 * ⚠️ **Р-142** («на проверке после срока — просрочена») ЭТИМ НЕ ТРОНУТ:
 * `isOverdue` спрашивает `isOpenStatus`, а не этот предикат, и сданная на
 * проверку работа со вчерашним сроком по-прежнему просрочена.
 */
export function isHandedOff(status: WorkStatus): boolean {
  return status === WorkStatus.ON_REVIEW;
}

/**
 * Работа просрочена: срок в прошлом И она не завершена.
 *
 * ТЗ-5.3-c дословно: «просрочка вычисляется по сроку и отображается признаком,
 * отдельным статусом не является». Отсюда два следствия, и второе теряют чаще:
 * признак показывается ВМЕСТЕ со статусом, а не вместо него; и завершённая
 * работа со сроком в прошлом просроченной НЕ является — её уже сдали.
 */
export function isOverdue(dueDate: string, today: string, status: WorkStatus): boolean {
  // Проверка ДО сравнения: `'' < '2026-08-09'` истинно лексикографически, то
  // есть пустой срок читался бы как просроченный.
  assertCalendarDate(dueDate, 'dueDate');
  assertCalendarDate(today, 'today');
  return isOpenStatus(status) && dueDate < today;
}

/**
 * Незавершённая работа, срок которой попадает в окно `[today; today + days]`.
 *
 * ═══ ЗАЧЕМ ОТДЕЛЬНАЯ ФУНКЦИЯ, А НЕ `isOpenStatus && dueDate <= …` ПО МЕСТУ
 *
 * Потребитель — лента «Сроки» сводки (Блок 2 · Ф7 · В13′): она показывает
 * просроченное (`isOverdue`) И приближающееся. Вторую половину пришлось бы
 * писать вызывающему — вместе с арифметикой окна (`addDaysIso`) и с обоими
 * концами включительно. Ровно там и разъезжаются два экрана: один считает
 * окно от «сегодня», другой от «завтра», и разницу видно только на границе.
 *
 * ═══ ⚠️ ЧЕГО ЭТА ФУНКЦИЯ НЕ ДАЁТ — И ЭТО БЫЛО ЗАПИСАНО НЕВЕРНО ═══════════
 *
 * 🚫 Прежняя редакция обещала: ~~«Определение одно и живёт здесь, в доме шкалы;
 * сторож `no-second-aggregate` этот дом и охраняет»~~. **Обещание ложное, и
 * опровергнуто прогоном** (адвокат дьявола В13′, пять пробников в
 * `summary.service.ts`, каждый — вставка одной строки и откат):
 *
 *   | вставленная форма                                  | сторож  |
 *   | -------------------------------------------------- | ------- |
 *   | `status === 'DONE' ? 'DONE' : 'IN_PROGRESS'`        | ЗЕЛЁНЫЙ |
 *   | `status === 'DONE' \|\| status === 'ON_REVIEW'`     | красный |
 *   | `{ status: { not: 'DONE' } }` (объект `where`)      | ЗЕЛЁНЫЙ |
 *   | `{ status: { in: ['NOT_STARTED', …] } }`            | красный |
 *   | **`status !== 'DONE'`** — ровно то, что «предотвращено» | ЗЕЛЁНЫЙ |
 *
 * То есть сторож ловит ДВА И БОЛЕЕ состояния в сравнении (его собственный
 * порог `definesScaleRule`) и массив-шкалу; ОДНО сравнение ему невидимо — он
 * сам это признаёт в шапке («реализация, обходящаяся одним сравнением, …
 * проходит обе проверки»). Значит «второй реализации нет» (Р-135) держится
 * **ревью, а не машиной**, и вынос предиката в `isOpenStatus` — удобство и
 * один адрес правки, а не гейт. Писать иначе значит обещать защиту, которой
 * в репозитории нет.
 *
 * ⚠️ ОКНО ВКЛЮЧАЕТ ОБА КОНЦА. `dueDate === today` — «срок сегодня», он в окне и
 * при этом НЕ просрочен (`isOverdue` требует строгого `<`). Граница между
 * «сегодня» и «вчера» проходит ровно здесь, и разъехаться двум функциям нельзя:
 * работа со вчерашним сроком попадает в `isOverdue` и не попадает сюда, работа
 * со сроком «сегодня» — наоборот. Пересечения множеств нет по построению.
 *
 * ⚠️ ШИРИНА ОКНА — ПАРАМЕТР, А НЕ КОНСТАНТА. Признак «Срочно» в карточке —
 * это `isUrgent` (одни сутки), и он единственный авторитет срочности (долг
 * #85). Лента — ПОТРЕБИТЕЛЬ окна, а не второе правило срочности, поэтому число
 * приходит от вызывающего и в этом файле не записано.
 *
 * @param days Ширина окна в сутках; `0` — только сегодняшний срок.
 */
export function isDueWithin(
  dueDate: string,
  today: string,
  days: number,
  status: WorkStatus,
): boolean {
  assertCalendarDate(dueDate, 'dueDate');
  assertCalendarDate(today, 'today');
  if (!Number.isInteger(days) || days < 0) {
    throw new RangeError(`Ширина окна должна быть целой и неотрицательной, получено: ${days}`);
  }
  return isOpenStatus(status) && dueDate >= today && dueDate <= addDaysIso(today, days);
}

/**
 * Что показать рядом со сроком: `'overdue'`, `'urgent'` или ничего.
 *
 * ─── ЗАЧЕМ ОТДЕЛЬНАЯ ФУНКЦИЯ, ЕСЛИ ЕСТЬ `isUrgent` ────────────────────────
 *
 * `isUrgent` — это `dueDate <= today + 1`, и она ИСТИННА на просроченной
 * работе тоже (вчерашний срок тем более «не позже завтра»). Сервер считает
 * поле `ProjectTeamWorkSchema.urgent` ровно ею (`project-team.service.ts:336`),
 * и менять это нельзя: авторитет один.
 *
 * Но план фазы Ф3 требует различать состояния дословно — «срочная — ещё не
 * просроченная» (критерий ТЗ-5.3-c в В3). Совмещается это ровно здесь, на
 * уровне ПОКАЗА: правило одно (`isUrgent`), а взаимное исключение вводится
 * приоритетом просрочки над срочностью. Разведи их по компонентам — получишь
 * два экрана, на которых вчерашний срок подписан по-разному.
 *
 * Завершённая работа не несёт НИ ОДНОГО признака: ни просрочки (см.
 * `isOverdue`), ни срочности — торопить нечего.
 */
export type UrgencyTone = 'overdue' | 'urgent' | null;

export function urgencyTone(dueDate: string, today: string, status: WorkStatus): UrgencyTone {
  assertCalendarDate(dueDate, 'dueDate');
  assertCalendarDate(today, 'today');
  if (status === WorkStatus.DONE) return null;
  if (isOverdue(dueDate, today, status)) return 'overdue';
  // `isUrgent` и `addDaysIso` — контракт Ф2 с ПОТРЕБИТЕЛЕМ НА СЕРВЕРЕ
  // (`project-team.service.ts:336`), и их снисходительность к мусору
  // (`addDaysIso` возвращает вход как есть) здесь НЕ меняется: правка
  // сигнатуры или поведения общей функции ради формы показа — вторжение в
  // чужую волну. Защита стоит выше по стеку, на входе в показ, и этого
  // достаточно: дальше `isUrgent` получает уже проверенные даты.
  return isUrgent(dueDate, today) ? 'urgent' : null;
}

// ─── Агрегированный статус узла раздела (Ф3 · В3a) ────────────────────────
//
// ЕДИНСТВЕННАЯ реализация правила. Зовут её двое: дерево разделов в карточке
// объекта (В3, клиент) и сводка по объекту (В13, Ф7 — там на сервере). Две
// реализации разошлись бы ТИХО: карточка и сводка показали бы разные цифры, и
// решать, какая права, было бы нечем (риск 2 плана фазы).
//
// ⚠️ САМОГО ПРАВИЛА АГРЕГИРОВАНИЯ НЕТ НИ В ОДНОМ ИСТОЧНИКЕ. Проверено
// чтением: ТЗ (`TZ.md:240`) задаёт НАБОР статусов раздела — «не начат, в
// работе, на проверке, готово» — и говорит, что просрочка отдельным статусом
// не является; КАК четыре статуса работ сворачиваются в один статус раздела,
// не сказано ни в ТЗ, ни в журнале решений, ни в плане (§6.3 задаёт только
// сигнатуру). Корневой план (`PLAN.md:637`) отсылает к «чистой `nextStatus`
// из `tracker-meta.ts`» — этого файла в репозитории НЕТ ВОВСЕ (код партнёра).
//
// Значит правило ниже — РЕШЕНИЕ ЭТОЙ ВОЛНЫ, а не перенос. Оно записано здесь
// целиком именно поэтому: переоткрывать его будут по этому тексту.

/** Шкала от «не начато» к «готово». Порядок несущий — по нему берётся минимум. */
export const WORK_STATUS_ORDER: readonly WorkStatus[] = Object.freeze([
  WorkStatus.NOT_STARTED,
  WorkStatus.IN_PROGRESS,
  WorkStatus.ON_REVIEW,
  WorkStatus.DONE,
]);

/** Позиция статуса на шкале. Экспортируется: шкала прогресса считает по ней. */
export function workStatusRank(status: WorkStatus): number {
  return WORK_STATUS_ORDER.indexOf(status);
}

/**
 * ЗАПРЕЩЁННЫЙ ПЕРЕХОД ПО ШКАЛЕ — решение **Р-75 «Из „закрыто" работа в „не
 * начато" не возвращается»** (Блок 2 · Ф5 · В7.5).
 *
 * ═══ ПОЧЕМУ ПРАВИЛО ЖИВЁТ ЗДЕСЬ, А НЕ В СЕРВИСЕ ═════════════════════════════
 *
 * ⚠️ ПЕРВАЯ РЕДАКЦИЯ В7.5 НАПИСАЛА ЭТО УСЛОВИЕ ПРЯМО В `changeStatus`, И
 * СТОРОЖ `apps/web/src/components/work/no-second-aggregate.test.ts` (ПРОВЕРКА
 * 2) НА НЁМ ПОКРАСНЕЛ — правильно покраснел. Условие сравнивает ДВА разных
 * состояния между собой, то есть заводит ОТНОШЕНИЕ на шкале, а дом отношений
 * на шкале — этот файл: здесь `WORK_STATUS_ORDER`, здесь `workStatusRank`,
 * здесь единственная законная реализация агрегата.
 *
 * ⚠️ И ЭТО НЕ УСТУПКА СТОРОЖУ, А ЕГО ПРЕДМЕТ. У правила есть ВТОРОЙ
 * потребитель, названный поимённо ещё до того, как правило было написано:
 * **Р-75, граница 2** обещает веб-половину («один `if` плюс одна строка
 * словаря» в `denialReason`), а ответ владельца по **С-12** («Только сервер»)
 * увёз её ДОЛГОМ в план Ф5, раздел 8.3. Правило, оставленное в
 * `work.service.ts`, дождалось бы там второй копии — ровно того расхождения,
 * ради которого сторож и написан. Отсюда предикат импортируется обеими
 * половинами, и переписывать его вебу будет нечего.
 *
 * ═══ ЧТО ЭТА ФУНКЦИЯ НЕ ДЕЛАЕТ ═════════════════════════════════════════════
 *
 * ⚠️ ОНА НЕ РУБЕЖ. Рубеж — **на сервере** (Р-75, граница 1): отказ ставит
 * `changeStatus` кодом `WORK_DONE_CANNOT_RESET` и статусом 422. Предикат —
 * только формулировка «куда нельзя»; вызвавший его и не отказавший ничего не
 * запретил. Веб-половина, когда до неё дойдут руки, тоже остаётся ПОДСКАЗКОЙ:
 * правило, живущее только в вебе, отменяется одним `curl`.
 *
 * ⚠️ ОНА НЕ МАТРИЦА ПЕРЕХОДОВ. Матрицы разрешённых переходов в системе нет
 * (это наблюдение записано и во второй поправке к **Р-66**, и в самом Р-75);
 * здесь названа ОДНА запрещённая пара, а не перечислены разрешённые. Все
 * прочие пятнадцать ячеек по-прежнему законны — в том числе «в работе → не
 * начата» и «на проверке → не начата»: запрет стоит на ПАРЕ, а не на целевом
 * состоянии (Р-75, «Условие — пара „из закрыто В не начато"»).
 *
 * ⚠️ ВТОРУЮ ЗАПРЕЩЁННУЮ ПАРУ СЮДА ДОПИСАТЬ МАЛО. Вызывающий отвечает одним
 * кодом отказа `WORK_DONE_CANNOT_RESET` с одним текстом, потому что пара
 * ровно одна. Появление второй пары обязано принести и второй код — иначе
 * человек получит текст про «не начато» на переходе, к «не начато»
 * отношения не имеющем. Тогда булев ответ здесь сменится на причину отказа,
 * а не «просто добавится ещё один `||`».
 *
 * ⚠️ ПРАВИЛО СТОИТ НА МУТАЦИИ, А НЕ НА ДАННЫХ (Р-75, граница 3). Строки,
 * прошедшие этот переход до запрета, остаются как есть; ни миграции, ни
 * обратной подгонки состояний под него нет и не заводится.
 *
 * @param from Состояние ДО мутации (то, что лежит в строке).
 * @param to Состояние ПОСЛЕ (то, что пришло запросом).
 */
export function isForbiddenStatusTransition(from: WorkStatus, to: WorkStatus): boolean {
  return from === WorkStatus.DONE && to === WorkStatus.NOT_STARTED;
}

/**
 * Вход агрегата.
 *
 * ⚠️ `archivedAt` ЗДЕСЬ ЕСТЬ, И ЭТО ПРАВКА ПО СУЩЕСТВУ (Ф3 · В3a, находка
 * круга ревью). Прежде тип нёс только `status` и `dueDate`, а докблок функции
 * ТРЕБОВАЛ «архивные сюда не подаются» — то есть предпосылку не обеспечивала
 * ни функция, ни тип, и вызывающий не мог даже проверить, соблюл ли он её.
 * Держалось всё на том, что сервер архивные строки не отдаёт; первый же
 * вызывающий с `includeArchived=true` (а такой параметр у `GET /works` есть)
 * получил бы узел, навсегда застрявший в «не начато».
 *
 * Поле НЕОБЯЗАТЕЛЬНОЕ: вызывающему, который уже отфильтровал, ничего менять не
 * нужно, а тот, кто не фильтровал, теперь защищён.
 */
export interface AggregatableWork {
  readonly status: WorkStatus;
  readonly dueDate: string;
  /** Непустое — работа снята; в статусе узла она НЕ участвует. */
  readonly archivedAt?: string | null;
}

/** Выход агрегата — статус узла плюс признаки поверх него. */
export interface SectionAggregate {
  /** Один из четырёх статусов ТЗ-5.3. */
  readonly status: WorkStatus;
  /** Хотя бы одна незавершённая работа узла просрочена. */
  readonly overdue: boolean;
  /** Хотя бы одна незавершённая работа срочна и при этом НЕ просрочена. */
  readonly urgent: boolean;
  /** Всего работ в узле и сколько из них завершено — прогресс узла. */
  readonly total: number;
  readonly done: number;
}

/**
 * Свернуть работы узла в один статус раздела.
 *
 * ═══ УЗЕЛ-ГРУППА АГРЕГИРУЕТСЯ ПО ВСЕМ РАБОТАМ ПОД НИМ, А НЕ КОМПОЗИЦИЕЙ ═══
 *
 * Дерево карточки объекта двухуровневое: **группа справочника → раздел →
 * работы**. Для узла ГРУППЫ подаётся плоский список работ ВСЕХ её разделов
 * целиком. Складывать агрегаты детей нельзя, и это не стиль:
 *
 *   - `status` при композиции ассоциативен — совпадёт;
 *   - `total` и `done` — НЕТ: композиция посчитала бы разделы, а не работы,
 *     и группа из двух разделов по три работы показала бы «2», а не «6»;
 *   - `overdue` и `urgent` вычислить из детских агрегатов **невозможно в
 *     принципе**: они выводятся из `dueDate` каждой работы, а в `SectionAggregate`
 *     срока нет вовсе. Композиция потребовала бы выдумать срок.
 *
 * Правило записано здесь, потому что потребителей у функции ДВА и они в разных
 * фазах: дерево (В3, клиент) и сводка по объекту (В13, Ф7, сервер). Прочитав
 * «узел» по-разному, они разошлись бы в цифрах — ровно то расхождение, ради
 * устранения которого функция единственная. Закреплено тестом, где композиция
 * и прямой расчёт дают РАЗНЫЕ числа и правильным объявлен прямой.
 *
 * ─── ПРАВИЛО, ДОСЛОВНО ────────────────────────────────────────────────────
 *
 * 1. **узел без работ → «не начато».** Раздел заведён, вести его некому —
 *    это и есть «не начат». Отдельного состояния «пусто» ТЗ не знает, и
 *    выдумывать его значило бы завести пятый статус раздела в обход решения
 *    заказчика 3 (их ровно четыре);
 * 2. **иначе берётся МИНИМУМ по шкале** — узел не готов, пока не готова
 *    последняя его работа. Максимум был бы прямой ложью: раздел с одной
 *    сданной и четырьмя не начатыми работами показывал бы «готово»;
 * 3. **исключение из п. 2: минимум «не начато» + есть хоть одна сдвинувшаяся
 *    работа → «в работе».** Без этой строки раздел, где половина работ уже
 *    на проверке, значился бы «не начат» — то есть узел не менялся бы месяцами
 *    и выглядел бы при этом исправным.
 *
 * ⚠️ ПРОСРОЧКА В СТАТУС НЕ ВХОДИТ — она признак (`overdue`), ТЗ-5.3-c. Свернув
 * её в статус, мы завели бы пятое состояние раздела, которого заказчик не
 * заводил, и потеряли бы «на проверке» ровно там, где оно важнее всего:
 * просроченная работа на проверке всё ещё ждёт приёмки.
 *
 * ⚠️ АРХИВНЫЕ РАБОТЫ ОТСЕИВАЕТ САМА ФУНКЦИЯ, а не вызывающий (правка Ф3 · В3a).
 * Снятая работа не участвует в статусе узла — снятие это архивация (решение
 * заказчика 6), — и подмешанная сюда она удержала бы узел в «не начато»
 * навсегда. Прежде правило стояло ТОЛЬКО в этом абзаце, и обеспечить его было
 * нечем: тип архивности не нёс.
 *
 * ⚠️ УЗЕЛ, У КОТОРОГО ВСЕ РАБОТЫ АРХИВНЫЕ, РАВЕН ПУСТОМУ — «не начато», ноль
 * из нуля. Это осознанно: снятые работы не существуют для статуса, и «готово»
 * на разделе, где всё сняли, было бы прямой ложью.
 *
 * @param works Живые работы узла. Порядок не важен — функция коммутативна.
 * @param today Серверное «сегодня» (`YYYY-MM-DD`). НЕ `new Date()`: с 00:00 до
 *   03:00 MSK «сегодня» у браузера и сервера разное, и признак просрочки
 *   загорался бы на сутки раньше или позже.
 */
export function aggregateSectionStatus(
  works: readonly AggregatableWork[],
  today: string,
): SectionAggregate {
  assertCalendarDate(today, 'today');
  // Отсев ПЕРВЫМ действием: дальше по функции архивной строки не существует.
  works = works.filter((w) => w.archivedAt === undefined || w.archivedAt === null);
  if (works.length === 0) {
    return { status: WorkStatus.NOT_STARTED, overdue: false, urgent: false, total: 0, done: 0 };
  }

  // Минимум и максимум держатся ЗНАЧЕНИЯМИ, а не индексами: обратный поиск
  // `WORK_STATUS_ORDER[rank]` даёт `WorkStatus | undefined`, и приведение
  // этого `undefined` было бы утверждением «индекс всегда в границах»,
  // которое компилятор проверить не может, а читатель — не заметит.
  let minStatus: WorkStatus = WorkStatus.DONE;
  let maxRank = 0;
  let done = 0;
  let overdue = false;
  let urgent = false;

  for (const w of works) {
    const rank = workStatusRank(w.status);
    if (rank < workStatusRank(minStatus)) minStatus = w.status;
    if (rank > maxRank) maxRank = rank;
    if (w.status === WorkStatus.DONE) done += 1;
    const tone = urgencyTone(w.dueDate, today, w.status);
    if (tone === 'overdue') overdue = true;
    else if (tone === 'urgent') urgent = true;
  }

  // П. 3: «не начато» держится только пока НИ ОДНА работа не сдвинулась.
  const status =
    minStatus === WorkStatus.NOT_STARTED && maxRank > 0 ? WorkStatus.IN_PROGRESS : minStatus;

  return { status, overdue, urgent, total: works.length, done };
}

/** Одна доля распределения: состояние и сколько узлов в нём. */
export interface SectionStatusTally {
  readonly status: WorkStatus;
  readonly count: number;
}

/**
 * Распределение узлов по четырём состояниям — В ПОРЯДКЕ ШКАЛЫ и с нулями.
 *
 * ═══ ПОЧЕМУ ЭТО ЗДЕСЬ, А НЕ В СЕРВИСЕ СВОДКИ ИЛИ В КОМПОНЕНТЕ ═════════════
 *
 * Потребитель — плитка «Разделы по бюро» (Блок 2 · Ф7 · В13′): сервер считает
 * доли, клиент рисует полосу и легенду. Обеим сторонам нужен ПОРЯДОК шкалы, а
 * порядок в системе один и живёт в этом файле (`WORK_STATUS_ORDER`). Посчитай
 * сервер распределение объектом `{ NOT_STARTED: n, … }`, и клиент, чтобы
 * нарисовать полосу слева направо, завёл бы СВОЙ массив состояний — вторую
 * шкалу, ровно ту, которую ловит сторож `no-second-aggregate` (признак B).
 * Поэтому наружу отдаётся УПОРЯДОЧЕННЫЙ список: клиенту остаётся `map`, и
 * порядка он не знает вовсе.
 *
 * ⚠️ НУЛИ ОСТАЮТСЯ В ВЫДАЧЕ. Состояние, в котором сегодня нет ни одного узла,
 * возвращается со счётом `0`, а не выпадает: легенда из четырёх строк не
 * должна менять длину от того, что сегодня никто ничего не сдал, — иначе
 * читатель решит, что состояние отменили. Полоса рисует нулевую долю нулевой
 * шириной сама.
 *
 * ⚠️ ЭТО НЕ АГРЕГАТ И ЕГО НЕ ЗАМЕНЯЕТ. `aggregateSectionStatus` сворачивает
 * работы ОДНОГО узла в одно состояние; здесь складываются уже посчитанные
 * состояния РАЗНЫХ узлов. Отношения на шкале (минимум, сдвиг) функция не
 * заводит вовсе — она только считает.
 *
 * @param statuses Состояния узлов; порядок входа не важен.
 */
export function tallySectionStatuses(
  statuses: readonly WorkStatus[],
): readonly SectionStatusTally[] {
  const counts = new Map<WorkStatus, number>();
  for (const s of statuses) counts.set(s, (counts.get(s) ?? 0) + 1);
  return WORK_STATUS_ORDER.map((status) => ({ status, count: counts.get(status) ?? 0 }));
}

// ─── Запросы на запись ────────────────────────────────────────────────────

/**
 * `POST /works` (маршрут приезжает в В2a).
 *
 * `.strict()` — смуглённые `createdById` / `version` / `sortOrder` отвергаются:
 * автор приходит из JWT, версию правит только расширение блокировок, порядок
 * задаёт перетаскивание.
 *
 * ЗЕРКАЛО ТРЁХ CHECK-ов, а не замена им. Правило держит БАЗА; здесь оно
 * повторено только затем, чтобы пользователь увидел понятный текст вместо
 * 23514. Ослабление любой из ЭТИХ ТРЁХ строк НЕ откроет запись — оно лишь
 * заменит внятную ошибку формы на невнятную ошибку драйвера.
 *
 * 🚫 ⚠️ И РОВНО ЭТО ПЕРЕСТАЛО БЫТЬ ВЕРНЫМ ПРО ВСЮ СХЕМУ — Ф4 · В-ПР-1, Р-36.
 * Прежняя редакция говорила «ослабление ЛЮБОЙ строчки ниже», и формулировка
 * была верна на свою дату: правил было три, и за каждым стоял CHECK. Теперь
 * `refine`-ов ЧЕТЫРЕ, и у четвёртого («у задачи должен быть объект») в базе НЕ
 * СТОИТ НИЧЕГО: `Work.projectId` остаётся `NULL`-разрешающей колонкой
 * осознанно (Р-36, граница 1 — `NOT NULL` отклонён, миграция на живых данных
 * выгоды сверх этой строки не даёт).
 *
 * ⚠️ ПОЧЕМУ ПРАВКА ОБЯЗАТЕЛЬНА, А НЕ КОСМЕТИЧЕСКАЯ: этот докблок стоит
 * ВПЛОТНУЮ к правилу, которое опровергает, и читается как разрешение снять
 * его — «база всё равно удержит». Не удержит. Четвёртый `refine` — ЕДИНСТВЕННЫЙ
 * рубеж на этом пути; снявший его откроет запись молча, и обнаружится это
 * строкой в базе, а не отказом.
 */
export const CreateWorkRequestSchema = z
  .object({
    kind: z.nativeEnum(WorkKind, ruEnum('Выберите вид работы')),
    assigneeId: UuidSchema,
    projectId: UuidSchema.optional(),
    sectionId: UuidSchema.optional(),
    title: TitleSchema,
    description: DescriptionSchema,
    startDate: IsoDateSchema,
    dueDate: IsoDateSchema,
    hoursEstimate: HoursEstimateSchema.optional(),
  })
  .strict()
  .refine((v) => isPeriodValid(v), {
    message: PERIOD_MESSAGE,
    path: ['dueDate'],
  })
  // CHECK 2 (046): у проектной работы проект обязателен.
  .refine((v) => v.kind !== WorkKind.PROJECT_WORK || v.projectId !== undefined, {
    message: 'У проектной работы должен быть объект',
    path: ['projectId'],
  })
  // CHECK 1 (046): у проектной работы раздел обязателен.
  .refine((v) => v.kind !== WorkKind.PROJECT_WORK || v.sectionId !== undefined, {
    message: 'У проектной работы должен быть раздел',
    path: ['sectionId'],
  })
  // CHECK 3 (046): указан раздел ⇒ указан проект. НЕ тавтология: в базе у
  // задачи не обязательны ни тот, ни другой, и без этой строки задача с
  // разделом и без объекта прошла бы форму, а упала бы в базе.
  .refine((v) => v.sectionId === undefined || v.projectId !== undefined, {
    message: 'Раздел можно выбрать только вместе с объектом',
    path: ['projectId'],
  })
  /**
   * ⚠️ ЧЕТВЁРТАЯ ПРОВЕРКА — НЕ ЗЕРКАЛО CHECK-а, И ЭТО НАДО ЗНАТЬ ДО ПРАВКИ.
   * Три выше повторяют ограничения миграции 046; эта — правило ПРОДУКТА, за
   * которым в базе не стоит ничего: `Work.projectId` остаётся `NULL`-разрешающей
   * колонкой (Р-36, граница 1 — `NOT NULL` отклонён осознанно, миграция на живых
   * данных выгоды сверх этой строки не даёт).
   *
   * **Основание — Р-36 «ЗАДАЧ БЕЗ ОБЪЕКТА НЕ БУДЕТ».** Непроектная работа с
   * 11.08.2026 висит на служебном объекте «Внутренние работы» (Р-9, Р-10, Р-12),
   * то есть законного повода для задачи без объекта не осталось; шесть
   * унаследованных строк переводятся туда же.
   *
   * ⚠️ ПОМЕТКА ПЕРЕНЕСЕНА ИЗ РЕШЕНИЯ ДОСЛОВНО, А НЕ ОПУЩЕНА: ужесточение до
   * «задача тоже обязана нести объект» — ПРОЧТЕНИЕ ОРКЕСТРАТОРА из слов
   * заказчика «закрыть вопрос», а не его буквальный ответ (Р-36, граница 2).
   * Снимается удалением этой строки без последствий для остального решения —
   * ровно потому и стоит отдельным `refine`, а не слит с CHECK-ом 2 в общее
   * «объект обязателен всегда».
   *
   * ⚠️ ЧТО ЭТО НЕ ЗАКРЫВАЕТ: строка без объекта, УЖЕ лежащая в базе, законна и
   * читается всеми маршрутами по-прежнему. Запрещён только новый ввод.
   */
  .refine((v) => v.kind !== WorkKind.TASK || v.projectId !== undefined, {
    message: 'У задачи должен быть объект',
    path: ['projectId'],
  });
export type CreateWorkRequest = z.infer<typeof CreateWorkRequestSchema>;

/**
 * Название и описание В СХЕМЕ ПРАВКИ — очищаемые.
 *
 * Отличие от схемы создания несущее, и найдено оно кругом ревью прогоном.
 * `TitleSchema` нормализует `''` → `undefined`, а `undefined` в Prisma значит
 * «не менять». Значит на схеме создания всё верно (`undefined` = вставить
 * NULL), а на схеме правки однажды заполненное название нельзя было бы снять
 * НИЧЕМ: `''` игнорировался, `null` схема отвергала. И это при том, что пустое
 * название объявлено ЛЕГАЛЬНЫМ состоянием (§6.6) — интерфейс в этом случае
 * показывает актуальное имя раздела из каталога.
 *
 * Поэтому здесь `''` → `null` (как у `ComplexNameSchema`, и по той же причине),
 * а тип — `.nullable().optional()`: «не прислали» и «прислали пусто» различимы
 * и на уровне типа, а не только в голове.
 */
const ClearableTitleSchema = z.preprocess((v) => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}, z.string().min(1).max(300, 'Название не длиннее 300 символов').nullable());

const ClearableDescriptionSchema = z.preprocess((v) => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}, z.string().min(1).max(2000, 'Описание не длиннее 2000 символов').nullable());

/**
 * `PATCH /works/:id` (В2a). `kind` НЕ правится: смена вида означала бы
 * смену набора обязательных полей на живой строке — это создание новой работы,
 * а не редактирование. `status` правится отдельным маршрутом перехода (В2a),
 * поэтому его здесь нет.
 *
 * `projectId` и `sectionId` здесь тоже НЕТ, и это РЕШЕНИЕ, а не пропуск:
 * переноса работы на другой раздел или объект не существует. «Завели не тот
 * раздел» лечится архивацией и заведением заново; часы при этом остаются на
 * архивной работе. Названо вслух, потому что сценарий правдоподобный и
 * спросят про него на первом же демо.
 *
 * ⚠️ УСЛОВИЕ ДЛЯ В6 (Ф4), И ЕГО НАДО ЗНАТЬ ДО ПРАВКИ ЭТОЙ СХЕМЫ. Неизменность
 * вида работы, объекта и раздела держится ИСКЛЮЧИТЕЛЬНО отсутствием этих полей
 * здесь при `.strict()`. В базе её не держит НИЧТО: ни CHECK, ни триггер —
 * `kind`, `projectId` и `sectionId` там обычные колонки (план фазы §6.9 п. 4
 * говорит это про `kind` прямо). Барьер ОДИН, и снимается он добавлением поля
 * в эту форму — то есть ровно тем действием, каким В6 будет её расширять.
 * Расширяя, придётся либо сознательно разрешить перенос, либо завести второй
 * барьер; молчаливого третьего пути нет.
 */
export const UpdateWorkRequestSchema = z
  .object({
    version: z.number().int().nonnegative(),
    assigneeId: UuidSchema.optional(),
    title: ClearableTitleSchema.optional(),
    description: ClearableDescriptionSchema.optional(),
    startDate: IsoDateSchema.optional(),
    dueDate: IsoDateSchema.optional(),
    hoursEstimate: HoursEstimateSchema.nullable().optional(),
  })
  .strict()
  // Обе даты присланы вместе — сверяем здесь; присланная одна проверяется
  // сервером против сохранённой второй (схема второй не видит).
  .refine(
    (v) =>
      v.startDate === undefined ||
      v.dueDate === undefined ||
      isPeriodValid({ startDate: v.startDate, dueDate: v.dueDate }),
    { message: PERIOD_MESSAGE, path: ['dueDate'] },
  );
export type UpdateWorkRequest = z.infer<typeof UpdateWorkRequestSchema>;

// ─── Ответы ───────────────────────────────────────────────────────────────

/**
 * Работа в ответе сервера.
 *
 * `hoursEstimate` ЗДЕСЬ НЕТ НАМЕРЕННО — см. `HoursEstimateSchema`.
 *
 * `sectionName` — АКТУАЛЬНОЕ имя раздела из каталога, а не снимок: сервер
 * резолвит его на чтении. Именно поэтому у работы нет колонки-снимка имени.
 * Журнал изменений устроен ОБРАТНО (имена на момент события) — и это не
 * противоречие: карточка отвечает на вопрос «что это сейчас», журнал — «что
 * тогда сделали».
 */
export const WorkResponseSchema = z.object({
  id: UuidSchema,
  kind: z.nativeEnum(WorkKind),
  assigneeId: UuidSchema,
  assigneeName: z.string(),
  projectId: UuidSchema.nullable(),
  projectName: z.string().nullable(),
  sectionId: UuidSchema.nullable(),
  sectionName: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  startDate: IsoDateSchema,
  dueDate: IsoDateSchema,
  status: z.nativeEnum(WorkStatus),
  completedAt: IsoDateTimeSchema.nullable(),
  /** СТРОКА, а не число: `Decimal(20,10)` не помещается в double без потери
   *  младших разрядов, а именно они и различают соседей при перетаскивании.
   *  Маппер В2a обязан отдавать `.toFixed()`, а не `.toNumber()`. */
  sortOrder: z.string(),
  version: z.number().int().nonnegative(),
  archivedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  /**
   * «Я МОГУ ПОСТАВИТЬ „ГОТОВО" НА ЭТОЙ СТРОКЕ» — признак АКТОРОЗАВИСИМЫЙ
   * (Ф4 · В6-а, Р-18 + Р-20).
   *
   * ⚠️ ПРИЗНАК ПРИХОДИТ С СЕРВЕРА, А НЕ ВЫЧИСЛЯЕТСЯ ЭКРАНОМ, и это требование, а
   * не удобство. Правило зависит от строки И от объекта (кто ГИП, кто РО, какой
   * `kind`, кто постановщик) — фронт этих сведений не имеет. Второй вычислитель
   * правила на клиенте — долг «зеркало правил рядом с правилом» в готовом виде,
   * и разошёлся бы он В СТОРОНУ РАЗРЕШЕНИЯ: кнопка, которую сервер отвергает,
   * хуже отсутствующей.
   *
   * ⚠️ ЗНАЧЕНИЕ РАЗНОЕ ПО ВИДУ, И ЭТО НЕСУЩЕЕ. У `TASK` — «я постановщик»
   * (`createdById === actor.id`). У `PROJECT_WORK` — держатель права на ЭТУ
   * СТРОКУ, а НЕ авторство: сервер отвергнет автора потолком, и признак,
   * посчитанный как `createdById === actor.id` без охраны по `kind`, зажёг бы
   * у него действующую кнопку.
   *
   * 🚫 ⚠️ СТОЯЛО ~~«ПРЕЖНИЙ держатель права записи НА ОБЪЕКТЕ»~~ — снято
   * 24.08.2026 решением заказчика **Р-82 «Закрытие работы приводится к правилу
   * назначения: РО вправе закрыть работу своего сотрудника, а не любую на
   * объекте»**. Право на ОБЪЕКТ и право на РАБОТУ разошлись: у уровня
   * руководителя отдела второе сужено до работ СВОЕГО отдела
   * (`work.service.ts`, `mayWrite`). Текст правится здесь потому, что этот
   * докблок — единственный документ договора, который читают ОБЕ стороны, а
   * тесты пакета не гоняет ни CI, ни хуки: расхождение уехало бы молча.
   *
   * ⚠️ ⚠️ И СУЖЕНО ВТОРОЙ РАЗ 25.08.2026 — решением заказчика **Р-86 «ГИП
   * управляет объектом, а не работами отдела — административные роли выходят
   * из проектных работ целиком»**. У `PROJECT_WORK` признак теперь истинен
   * РОВНО у одного круга людей:
   *
   *     РО отдела исполнителя, вошедший в объект
   *     (уровень `MANAGER` ∧ строка `ProjectManager` этого объекта
   *      ∧ отдел исполнителя = отдел актора)
   *
   * `ADMIN`-уровень — включая ГИПа объекта и его помощника — получает `false`
   * ВСЕГДА, на любом объекте, включая свой. Область решения — все четыре роли
   * уровня `ADMIN` (ответ заказчика «А»).
   *
   * ⚠️ ЧИТАЮЩЕМУ ЭТОТ ДОГОВОР СО СТОРОНЫ ЭКРАНА: `canFinalize = false` у
   * администратора — это ПРАВИЛО, а не потеря контекста и не «сервер не
   * посчитал». Экран, дорисовывающий кнопку «по уровню» поверх этого поля,
   * отменяет решение — и отменяет молча, потому что отказ придёт только на
   * нажатии.
   *
   * ⚠️ ОН ГОВОРИТ О ПРАВЕ, А НЕ О ДОСТУПНОСТИ ПЕРЕХОДА. Снятая работа, уже
   * закрытая работа и порядок состояний — отдельные правила; экран складывает
   * их сам. Иначе поле стало бы вторым местом, где живёт вся машина состояний.
   */
  canFinalize: z.boolean(),
  /**
   * Оценка часов — ТОЛЬКО НА СТРОКАХ ЛИЧНОЙ СВЯЗИ, иначе `null` (Ф4 · В6-а).
   *
   * ⚠️ ПРЕЖДЕ ПОЛЯ ЗДЕСЬ НЕ БЫЛО НАМЕРЕННО, И ПОЛОВИНА ТОГО ОСНОВАНИЯ ОСТАЁТСЯ
   * В СИЛЕ — снята она наполовину, а не отменена. Причина запрета: `hoursEstimate`
   * маскируется даже в снимке аудита (`SENSITIVE_FIELDS.work`), и
   * `WorkOverlapQuerySchema` отказывается отдавать её сумму даже тому, кто
   * вправе видеть чужой счётчик занятости, — «маршрут, отдающий её сумму,
   * обошёл бы маскирование через арифметику».
   *
   * ⚠️ ПРОСТОЕ ПОЛЕ В ЭТОЙ ФОРМЕ ОТКРЫЛО БЫ РОВНО ТО ЖЕ, ТОЛЬКО БЕЗ АРИФМЕТИКИ:
   * `WorkResponse` — ОДНА форма на все строки, её отдаёт `GET /works`, а у
   * `MANAGER` видимость проектов пуста, то есть это оценка часов ПО КАЖДОЙ
   * РАБОТЕ БЮРО любому руководителю отдела.
   *
   * ⚠️ ЧТО ЛЕЧИТСЯ. Оценка ПИСАЛАСЬ, но не читалась: форма правки показывала бы
   * поле пустым и дальше одно из двух, оба плохи — `PATCH` без поля значение не
   * трогает (человек «стёр» оценку, а она осталась) либо шлёт `null` и обнуляет
   * молча. Довод сокрытия — про СУММУ ПО ЧУЖИМ строкам, а не про СВОЮ задачу.
   *
   * Условие: актор — исполнитель ЛИБО автор строки. `null` здесь означает «не
   * показываем», а не «не задана», и различить их снаружи нельзя намеренно.
   *
   * ⚠️ ВЫБОР ВОЛНЫ, А НЕ РЕШЕНИЕ ЗАКАЗЧИКА. Вопрос «кому отдавать» вынесен
   * владельцу открытым; до ответа исполняется вариант «только связанным лично».
   */
  hoursEstimate: z.number().nullable(),
});
export type WorkResponse = z.infer<typeof WorkResponseSchema>;

/**
 * ОТВЕТ МУТАЦИИ = работа + ДВА МНОЖЕСТВА ПОЛУЧАТЕЛЕЙ (Блок 2 · Ф5 · В7, п. 10).
 *
 * ⚠️ ЧИТАЮЩИЕ МАРШРУТЫ ЭТОЙ ФОРМЫ НЕ ОТДАЮТ — и это не оформление. Множества
 * описывают ТО, ЧТО СЛУЧИЛОСЬ В ЭТОЙ МУТАЦИИ, а у `GET` не случается ничего:
 * положив их в общую `WorkResponseSchema`, мы обязали бы список отвечать
 * пустыми массивами на каждую строку и приучили бы читателя, что пустой
 * `notBoundUserIds` значит «все привязаны».
 *
 * ⚠️ `notBoundUserIds` — ПОДМНОЖЕСТВО `queuedUserIds`, а не альтернатива ему.
 * По решению 19 «Бот не подключён» непривязанный получатель СТАВИТСЯ В ОЧЕРЕДЬ
 * и ждёт до 48 часов; он `queued`, а не `skipped`. Пометка на экране назначения
 * читается «получатель не привязал бота», и она НЕ означает, что уведомление
 * потеряно. Форма ответа обязана это передавать сама: множества два именно
 * затем, чтобы «поставлено» и «получатель не привязан» нельзя было склеить в
 * один флаг, как это делала первая редакция плана.
 *
 * ⚠️ Пустой `queuedUserIds` — законный ответ и НЕ ошибка: правка описания и
 * возврат работы из архива не порождают ни одной строки очереди. HTTP 200 в
 * любом случае.
 *
 * ⚠️ СМЕНА СОСТОЯНИЯ ИЗ ЭТОГО ПЕРЕЧНЯ УБРАНА, И ЭТО ДВЕ ПРАВКИ, А НЕ ОДНА
 * (Ф5 · В7.5). Прежний текст был ложен дважды: (1) после В7.5 смена состояния
 * строки ПОРОЖДАЕТ — пять новых видов ставятся именно там; (2) на её маршруте
 * этих полей больше НЕТ вовсе.
 *
 * ⚠️ `PATCH /works/:id/status` ОТВЕЧАЕТ ОБЫЧНОЙ `WorkResponseSchema`, БЕЗ
 * ОБОИХ МНОЖЕСТВ — решение владельца Р-76. Довод: сотрудник, нажавший «на
 * проверку» на проектной работе, получал бы идентификаторы ГИПа объекта, его
 * помощника и всех ведущих — включая тех, кого он в интерфейсе не видит, — а
 * `notBoundUserIds` сообщал бы сверх того, кто из них не привязал бота.
 *
 * ⚠️ ОБЛАСТЬ УЗКАЯ, И РАСШИРЯТЬ ЕЁ НЕЛЬЗЯ (Р-76, граница 1): на `POST /works`,
 * `PATCH /works/:id`, `POST /works/:id/archive` и `POST /works/:id/restore`
 * оба множества ОСТАЮТСЯ. Про эти маршруты владельца не спрашивали.
 *
 * ⚠️ И СЛЕДСТВИЕ ДЛЯ ПРОВЕРОК: пары, утверждающие адресатов, читают СТРОКИ
 * `NotificationOutbox`, а не тело ответа. Вернувший поля «ради теста» отменит
 * Р-76 молча и получит зелёный тест в доказательство.
 */
export const WorkMutationResponseSchema = WorkResponseSchema.extend({
  /** Кому в этой мутации поставлена строка очереди. */
  queuedUserIds: z.array(UuidSchema),
  /** У кого из них нет привязки бота на момент мутации. Подмножество. */
  notBoundUserIds: z.array(UuidSchema),
});
export type WorkMutationResponse = z.infer<typeof WorkMutationResponseSchema>;

// ─── Смена статуса (В2a) ──────────────────────────────────────────────────

/**
 * `PATCH /works/:id/status` — ОТДЕЛЬНЫЙ маршрут, а не поле в `UpdateWork`.
 *
 * Причина не в стиле: срочность работы ВЫВОДИТСЯ из срока, и если бы статус
 * менялся общей формой правки, исполнитель, доступ к которой у него есть,
 * переписал бы заодно сроки — а с ними и срочность. Разделив маршруты, мы
 * разделили и права: сроки правит тот, кто ведёт работу, статус — тот, кто её
 * делает.
 *
 * `completedAt` в запросе НЕТ и быть не может: его ставит и снимает сервер по
 * переходу, потому что БАЗА держит их согласованность двусторонним CHECK-ом
 * `work_done_completed_at` (046) — `DONE` без отметки и отметка без `DONE`
 * отвергаются одинаково.
 */
export const ChangeWorkStatusRequestSchema = z
  .object({
    status: z.nativeEnum(WorkStatus, ruEnum('Выберите состояние')),
    version: z.number().int().nonnegative(),
    /**
     * Порядок карточки в колонке — НЕОБЯЗАТЕЛЬНЫЙ спутник смены состояния
     * (Ф4 · В6, Р-19).
     *
     * ⚠️ ПОЛЕ ЖИВЁТ ИМЕННО ЗДЕСЬ, А НЕ В ОТДЕЛЬНОМ МАРШРУТЕ, И ПРИЧИНА
     * ПРОВЕРЯЕМАЯ. Перетаскивание между колонками меняет И состояние, И
     * порядок. Два вызова дали бы ДВЕ строки аудита и две версии — а критерий
     * волны требует ОДНОЙ строки аудита и ОДНОГО обновления с `where.version`,
     * то есть покраснел бы по построению. Отдельный
     * `PATCH /works/:id/position` вдобавок потребовал бы второго атома и
     * второго матричного спека и ВСЁ РАВНО двух вызовов при переносе.
     *
     * ⚠️ СТРОКА, А НЕ ЧИСЛО — по той же причине, что и в ответе: `Decimal(20,10)`
     * не помещается в double без потери МЛАДШИХ разрядов, а именно они
     * различают соседей после нескольких делений пополам.
     *
     * ⚠️ ГРАНИЦА ЗДЕСЬ — НЕ ГИГИЕНА, А РАЗНИЦА МЕЖДУ 400 И 500. Колонка
     * `Decimal(20,10)` даёт РОВНО 10 знаков до запятой; значение с одиннадцатью
     * приводит Postgres к `22003 numeric field overflow`, который ни один
     * фильтр в `code`-конверт не переводит — клиент получил бы 500 на опечатке.
     * Регулярное выражение держит обе границы сразу: до 10 цифр до точки и до
     * 10 после.
     *
     * Знак разрешён: вставка ПЕРЕД первой карточкой берёт значение левее нуля,
     * а `@default(0)` делает ноль обычной серединой колонки.
     */
    sortOrder: z
      .string()
      .regex(
        /^-?\d{1,10}(\.\d{1,10})?$/,
        'Порядок карточки — число не длиннее 10 знаков до запятой и 10 после',
      )
      .optional(),
  })
  .strict();
export type ChangeWorkStatusRequest = z.infer<typeof ChangeWorkStatusRequestSchema>;

// ─── Списки и счётчик занятости (В2a) ─────────────────────────────────────

/**
 * Строковый флаг запроса вместо `z.coerce.boolean()`.
 *
 * `z.coerce.boolean()` на строке запроса — ловушка: `Boolean('false') === true`,
 * то есть `?includeArchived=false` включал бы архив. Конвенция репозитория —
 * сравнение со строкой `'true'` (`section-catalog.controller.ts:84`); здесь она
 * выражена схемой, чтобы любое другое значение отвергалось, а не толковалось.
 */
const QueryTrueSchema = z.enum(['true', 'false']).optional();

/**
 * Поиск по ВИДИМОМУ ИМЕНИ строки — ОДНА форма на все три списка.
 *
 * Пустая и пробельная строка нормализуются в `undefined`, а не в `''`: иначе
 * `?search=` дало бы `contains: ''`, то есть предикат, пропускающий всё, и
 * поиск «ничего не ввели» молча отличался бы от «поиска нет». Потолок длины —
 * от `title` (300).
 *
 * ⚠️ ЗАГОЛОВОК ГОВОРИЛ «ПО НАЗВАНИЮ» И УСТАРЕЛ С Р-37 (18.08.2026). Схема от
 * этого не изменилась ни на строку — предмет сравнения выбирает СЕРВЕР
 * (`work.service.ts`, `visibleNameSearchWhere`), и правило там такое: название,
 * если оно есть, ИНАЧЕ имя раздела. Формулировка поправлена потому, что её
 * читают как требование, а требование теперь другое.
 */
const SearchSchema = z.preprocess((v) => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}, z.string().min(1).max(300, 'Строка поиска не длиннее 300 символов').optional());

/** `GET /works`. */
export const WorkListQuerySchema = z
  .object({
    projectId: UuidSchema.optional(),
    sectionId: UuidSchema.optional(),
    assigneeId: UuidSchema.optional(),
    kind: z.nativeEnum(WorkKind).optional(),
    status: z.nativeEnum(WorkStatus).optional(),
    /**
     * Поиск по видимому имени строки (Ф4 · В6; предмет сравнения уточнён Р-37).
     * GIN trgm-индекс `Work_title_trgm` (`schema.prisma`) существует с 046 и ДО
     * ЭТОЙ ВОЛНЫ СПАЛ: потребителя у него не было — схема была `.strict()` и
     * поля `search` не несла.
     *
     * ⚠️ ЭКРАН ВОЛНЫ ЭТИМ МАРШРУТОМ НЕ ПОЛЬЗУЕТСЯ (доска сидит на
     * `mine`/`assigned`), поэтому «индекс проснулся» и «поиск работает там, где
     * им пользуются» — РАЗНЫЕ утверждения, и путать их в отчёте нельзя.
     *
     * ⚠️ И ИНДЕКС ПОКРЫВАЕТ ТЕПЕРЬ НЕ ВЕСЬ ПРЕДИКАТ: вторая ветка Р-37 сверяет
     * `Section.name`, а `Work_title_trgm` построен по `title`. Второго индекса
     * фикс-пак не заводит — так решено (Р-37, граница 3), и записано это
     * затем, чтобы через год не обнаружили как сюрприз.
     */
    search: SearchSchema,
    /** По умолчанию архивные НЕ отдаются — списки и доски работают с живыми. */
    includeArchived: QueryTrueSchema,
  })
  .strict();
export type WorkListQuery = z.infer<typeof WorkListQuerySchema>;

/**
 * ОБЩАЯ ЧАСТЬ обеих личных форм запроса. Экспортируются схемы ниже, а не она:
 * маршрут читает СВОЮ, и общий предок нужен ровно затем, чтобы совпадающие поля
 * не разъехались.
 *
 * ⚠️ ПОРЯДОК СБОРКИ ЗАДАН И НЕ ПЕРЕСТАВЛЯЕТСЯ: базовый объект → `.extend()` →
 * сторож (`superRefine`) ПОСЛЕДНИМ на каждую схему. В зоде 3.23 `.superRefine()`
 * возвращает `ZodEffects`, у которого `.extend()` НЕТ ВОВСЕ, — наивная
 * последовательность «сторож, потом расширение» не собирается, а диагностика
 * `tsc` уводит на «сломанный контракт», который цел. Сегодня сторожей нет ни у
 * одной из двух схем; строка стоит ради первого, который появится.
 */
const personalWorkListQueryBase = z
  .object({
    projectId: UuidSchema.optional(),
    /**
     * Отбор по разделу (Ф4 · В-ПР, Р-31). Гейта видимости у него здесь НЕТ и не
     * нужно: личный маршрут и так отдаёт только строки, связанные с актором, —
     * раздел может лишь СУЗИТЬ эту выдачу.
     */
    sectionId: UuidSchema.optional(),
    kind: z.nativeEnum(WorkKind).optional(),
    status: z.nativeEnum(WorkStatus).optional(),
    search: SearchSchema,
    includeArchived: QueryTrueSchema,
  })
  .strict();

/**
 * `GET /works/mine` — «что поручено МНЕ» (Ф4 · В6-а, после Р-29 — «Моя работа»).
 *
 * 🚫 ⚠️ ЗДЕСЬ СТОЯЛО «ОДНА СХЕМА НА ДВА МАРШРУТА, А НЕ ДВЕ КОПИИ» — УТВЕРЖДЕНИЕ
 * ОТМЕНЕНО РЕШЕНИЕМ Р-29, А НЕ ПРИЗНАНО ОШИБОЧНЫМ. Посылка его была верна на
 * свою дату: оба маршрута кормили ОДНУ доску (переключатель «мои задачи / я
 * поручил», Р-19), и набор фильтров у неё был общим по построению. Р-29
 * переключатель снял: `mine` кормит «Мою работу», `assigned` — «Поручения», и
 * наборы отборов у них РАЗНЫЕ ПО РЕШЕНИЮ (Р-31: отбор по сотруднику — только на
 * «Поручениях»). Общей формы больше нет — есть общее ЯДРО
 * (`personalWorkListQueryBase`) и две схемы поверх него.
 *
 * ⚠️ `assigneeId` ЗДЕСЬ НЕТ, И ЭТО ДЕЙСТВУЮЩИЙ ЗАПРЕТ, А НЕ НАСЛЕДСТВО.
 * Исполнитель этого маршрута задан предикатом (актор), а не запросом; приняв
 * `assigneeId`, маршрут «мои задачи» стал бы маршрутом «чужие задачи» с
 * молчаливым сужением. Довод переезжает к {@link AssignedWorkListQuerySchema}
 * как основание разреза: там параметр законен, потому что там актор —
 * ПОСТАНОВЩИК, а исполнитель как раз и есть предмет отбора.
 *
 * ⚠️ РАЗРЕЗ ДЕРЖИТ ГРАНИЦУ САМ, БЕЗ ДИСЦИПЛИНЫ ФРОНТА. Критерий 3 Р-31 («отбор
 * по сотруднику есть на „Поручениях" ↔ и его НЕТ на „Моей работе"») здесь
 * доказывается `.strict()`: `?assigneeId=` на `mine` даёт 400. На общей схеме
 * его держало бы только то, что никто не прислал поле, — то есть ничто.
 *
 * ⚠️ `sectionId` ЗДЕСЬ ПОЯВИЛСЯ (Р-31) — прежний запрет на него ОТМЕНЁН тем же
 * решением. Раздел исполнителя не переопределяет и чужих строк не открывает.
 */
export const PersonalWorkListQuerySchema = personalWorkListQueryBase;
export type PersonalWorkListQuery = z.infer<typeof PersonalWorkListQuerySchema>;

/**
 * `GET /works/assigned` — «что я поручил ДРУГИМ» (Ф4 · В-ПР, Р-29 + Р-31).
 *
 * ⚠️ НАДМНОЖЕСТВО ЛИЧНОЙ СХЕМЫ, И КАЖДОЕ ЛИШНЕЕ ПОЛЕ ЗДЕСЬ — НЕСУЩЕЕ.
 * `assigneeId` — отбор по сотруднику, который Р-31 разрешает ТОЛЬКО на
 * «Поручениях»: там строки принадлежат разным людям, и вопрос «что я жду от
 * Иванова» осмыслен. На «Моей работе» все строки принадлежат одному человеку —
 * тому, кто смотрит, — и тот же отбор был бы украшением, изображающим работу.
 * `scope` — вид доски по Р-51; довод при самом поле ниже.
 *
 * 🚫 ⚠️ ЗДЕСЬ СТОЯЛО «РОВНО НА ОДНО ПОЛЕ» — УТВЕРЖДЕНИЕ ОТМЕНЕНО РЕШЕНИЕМ Р-51,
 * А НЕ ПРИЗНАНО ОШИБОЧНЫМ: на свою дату полей сверх личной схемы было ровно
 * одно. Счёт держать в тексте больше нечего — состав расхождения утверждается
 * прогоном (`test/work.test.ts`, «шире `mine` ровно на названный состав»), и
 * там же у каждого поля названо решение. Второй экземпляр счёта в комментарии
 * разошёлся бы со схемой ровно так, как этот и разошёлся.
 *
 * ⚠️ `.extend()` ПОВЕРХ `.strict()` СТРОГОСТЬ СОХРАНЯЕТ — то есть лишнее поле
 * отвергается и здесь. Проверено тестом рядом, а не принято на веру: без него
 * разрез схемы дал бы «Поручениям» щель, которой нет у `mine`.
 */
export const AssignedWorkListQuerySchema = personalWorkListQueryBase.extend({
  assigneeId: UuidSchema.optional(),
  /**
   * Р-51 — вид доски «Поручений»: «Мои поручения» либо «Все поручения».
   *
   * ⚠️ ОТСУТСТВИЕ КЛЮЧА ОЗНАЧАЕТ «МОИ», И ЭТО ГРАНИЦА 2 РЕШЕНИЯ Р-51, А НЕ
   * умолчание для красоты. Схема `.strict()`, ключ новый — значит его нет ни
   * у одной существующей закладки и ни у одного развёрнутого клиента. Сделай
   * умолчанием «все», и каждый из них **молча расширит выдачу**: на доске
   * появятся чужие строки без единого действия пользователя.
   *
   * ⚠️ ПОЛЕ ЖИВЁТ ЗДЕСЬ, А НЕ В ЛИЧНОЙ СХЕМЕ, ПО ТОМУ ЖЕ РАЗРЕЗУ, ЧТО
   * `assigneeId`: «Моя работа» показывает строки ОДНОГО человека — того, кто
   * смотрит, — и «все/мои» там не значит ничего. `.strict()` на `mine` даёт
   * 400 на этот ключ, то есть разрез держит себя сам, а не дисциплиной фронта.
   */
  scope: z.enum(['mine', 'all']).optional(),
});
export type AssignedWorkListQuery = z.infer<typeof AssignedWorkListQuerySchema>;

/**
 * `GET /works` и `GET /works/mine`.
 *
 * ─── `today` ЗДЕСЬ — НЕ УКРАШЕНИЕ, А ЕДИНСТВЕННЫЙ ЗАКОННЫЙ ИСТОЧНИК ───────
 *
 * Добавлено в Ф3 · В3a по находке круга ревью. Срочность и просрочка
 * ВЫЧИСЛЯЕМЫЕ, и считать их положено от серверного «сегодня»
 * (`queryTodayMsk`, Postgres в `Europe/Moscow`). Соседние контракты его уже
 * отдают — `WorkloadGanttResponse.today`, `ProjectTeamResponse.today`,
 * `WorkOverlapResponse.today`, — а два списочных маршрута отдавали только
 * `items`.
 *
 * **Чем это грозило.** Волна В5 («Моя работа», Ф4) стартует НЕ ДОЖИДАЯСЬ В3 и
 * В4 и живёт ровно на этих двух маршрутах. Примитивы `UrgencyChip` и
 * `PeriodFields` требуют `today` обязательным пропом — значит без поля здесь
 * у В5 не осталось бы законного источника вовсе, и она вынужденно написала бы
 * `new Date()`. Запрет на него держится комментарием в компоненте и тестом,
 * который живёт у ПРИМИТИВА, а не у потребителя: нарушение прошло бы зелёным.
 *
 * Поле обязательное, а не `.optional()`, намеренно: необязательное позволило
 * бы серверу его не прислать, и потребитель снова остался бы без «сегодня» —
 * но уже молча, с веткой `today ?? new Date()`, которая выглядит аккуратной.
 */
export const WorkListResponseSchema = z.object({
  items: z.array(WorkResponseSchema),
  /** Серверное «сегодня» (`Europe/Moscow`) — им же клиент считает срочность. */
  today: IsoDateSchema,
});
export type WorkListResponse = z.infer<typeof WorkListResponseSchema>;

/**
 * `GET /works/overlap` — «сколько уже висит на человеке в этом периоде».
 *
 * ЧАСОВ ЗДЕСЬ НЕТ НИ В КАКОМ ВИДЕ, и это не забывчивость: `hoursEstimate`
 * маскируется даже в снимке аудита (`SENSITIVE_FIELDS.work`), поэтому маршрут,
 * отдающий её сумму, обошёл бы маскирование через арифметику.
 */
export const WorkOverlapQuerySchema = z
  .object({
    userId: UuidSchema,
    startDate: IsoDateSchema,
    dueDate: IsoDateSchema,
    /** Правка периода СУЩЕСТВУЮЩЕЙ работы не должна конфликтовать сама с собой. */
    excludeWorkId: UuidSchema.optional(),
  })
  .strict()
  .refine((v) => isPeriodValid(v), {
    message: PERIOD_MESSAGE,
    path: ['dueDate'],
  });
export type WorkOverlapQuery = z.infer<typeof WorkOverlapQuerySchema>;

/**
 * Работа в счётчике и на графике — БЕЗ содержания, если её проект актору
 * невидим.
 *
 * Такая строка не выбрасывается, а маскируется: выброшенная, она занизила бы
 * занятость человека, и руководитель назначил бы работу поверх чужой.
 *
 * МАСКИРУЕТСЯ И `title` — шире, чем сказано в плане (там названы только
 * `projectName` и `sectionName`). Довод: название работы — свободный текст,
 * написанный ВНУТРИ невидимого проекта, и утечка через него шире, чем через имя
 * проекта, которое мы прячем. Занятость при этом остаётся видна полностью:
 * период, вид и состояние не маскируются, они и есть предмет.
 */
export const WorkRefSchema = z.object({
  workId: UuidSchema,
  kind: z.nativeEnum(WorkKind),
  projectId: UuidSchema.nullable(),
  projectName: z.string().nullable(),
  sectionId: UuidSchema.nullable(),
  sectionName: z.string().nullable(),
  title: z.string().nullable(),
  startDate: IsoDateSchema,
  dueDate: IsoDateSchema,
  status: z.nativeEnum(WorkStatus),
  /** `true` — проект строки актору невидим, имена вычищены. */
  masked: z.boolean(),
});
export type WorkRef = z.infer<typeof WorkRefSchema>;

export const WorkOverlapResponseSchema = z.object({
  total: z.number().int().nonnegative(),
  /** Срочные: срок ≤ «сегодня» + 1. Считается от серверного `today`. */
  dueSoon: z.number().int().nonnegative(),
  projectWork: z.number().int().nonnegative(),
  tasks: z.number().int().nonnegative(),
  absenceDaysInPeriod: z.number().int().nonnegative(),
  items: z.array(WorkRefSchema),
  /** Серверное «сегодня» (`Europe/Moscow`) — им же клиент считает срочность. */
  today: IsoDateSchema,
});
export type WorkOverlapResponse = z.infer<typeof WorkOverlapResponseSchema>;

/**
 * `POST /works/:id/archive` и `/restore` — тело несёт только версию.
 *
 * Версия обязательна и на архивации: снятие работы — такая же правка строки,
 * как остальные, и «последний записавший победил» здесь стоило бы дороже. У
 * `DELETE /sections/:id` версия читается из свежей строки, потому что у DELETE
 * тела нет вовсе; здесь маршрут — `POST`, и повода терять проверку нет.
 */
export const WorkVersionOnlyRequestSchema = z
  .object({ version: z.number().int().nonnegative() })
  .strict();
export type WorkVersionOnlyRequest = z.infer<typeof WorkVersionOnlyRequestSchema>;
